import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { styled } from '@components-shared/src/stitches';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { LikeReleaseButton } from '@components/Likes/Button';
import { useLikeRelease } from '@components/Likes/useLikeRelease';
import { ChartCard } from '@components/basics/ChartCard';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { FullSizeChartCardFragmentDoc, getFragment } from '@graphql/reactQuery';
import { usePlayButton } from '@hooks/usePlayButton';
import { trackClickEvent } from '@lib/analytics';
import { useAudioController } from '@store/audio/AudioController';
import { setUpNextTrackIds } from '@store/audio/AudioQueue';
import { getStaticCoverImage } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { ChartCardThreeDotMenu } from './ChartCardThreeDots';
import { ChartDetails } from './ChartDetails';
import { Stats } from './Stats';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FullSizeChartCard on ReleaseChartRank {
    id
    trendingIndicator
    currentRank
    release {
      id
      type
      title
      webappUri
      staticCoverImage {
        id
        url
      }
      coverImage {
        id
        url
      }
      artist {
        id
        name
        webappUri
      }
      track {
        id
      }
      topCollectors {
        user {
          id
          avatar {
            id
            url
          }
          username
          publicAddress
          webappUri
        }
        nftsCount
      }
      ...Stats
      ...ChartsThreeDotReleaseInfo
    }
  }
`);

export const FullSizeChartCard = ({
  chartInfo,
  expandedId,
  setExpandedId,
  getNextTrackIds,
  collectorAmount,
}: {
  chartInfo: FragmentType<FullSizeChartCardFragmentDoc>;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  getNextTrackIds: () => string[];
  collectorAmount: number;
}) => {
  const chart = getFragment(FullSizeChartCardFragmentDoc, chartInfo);

  const {
    currentRank,
    trendingIndicator,
    release: {
      id: releaseId,
      type: releaseType,
      track,
      artist,
      coverImage,
      staticCoverImage,
      webappUri,
      title,
    },
  } = chart;
  const { isLiked } = useLikeRelease({
    id: releaseId,
    source: 'Chart',
  });

  const { activeTrackId, playing } = useAudioController();
  const { desktop, tablet, tabletLandscape, hasAppMounted } = MediaQuery.useContainer();
  const songUrl = webappUriPath(webappUri);
  const artistUrl = webappUriPath(artist.webappUri);

  const {
    onPlayClick,
    isLoading,
    ref: playButtonPrefetchRef,
  } = usePlayButton({
    trackId: track.id,
    isAlbum: releaseType === 'ALBUM',
  });

  const playAndavoidBubblingEvent = useStableCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    await onPlayClick();
    setUpNextTrackIds(getNextTrackIds());
  });

  const size = tablet || !hasAppMounted ? 'tablet' : 'mobile';

  return (
    <ChartCard
      minimal={false}
      expandedId={expandedId}
      setExpandedId={setExpandedId}
      isPlaying={playing && activeTrackId === track.id}
      id={chart.id}
      key={releaseId}
      accordionDetails={
        tablet ? (
          <ChartDetails chartRankId={chart.id} trackTitle={title} artistName={artist.name} />
        ) : undefined
      }
      liked={isLiked}
    >
      <ChartCard.Cover
        isBufferingChart={isLoading}
        coverLink={songUrl}
        image={
          <ChartCard.Cover.Image
            desktop={tabletLandscape}
            src={getStaticCoverImage({
              coverImage: coverImage.url,
              staticCoverImage: staticCoverImage?.url,
            })}
            unoptimized={false}
            preloadImage={false}
          />
        }
        onPlayButtonClick={playAndavoidBubblingEvent}
        rank={currentRank}
        change={trendingIndicator}
        playButtonPrefetchRef={playButtonPrefetchRef}
      />
      <ChartCard.InfoAndCollectors minimal={false}>
        <ChartCard.Info
          header={
            <AccessibleLink onClick={e => e.stopPropagation()} pathnameUri={songUrl}>
              {title}
            </AccessibleLink>
          }
          subheader={
            <AccessibleLink onClick={e => e.stopPropagation()} pathnameUri={artistUrl}>
              {artist.name}
            </AccessibleLink>
          }
          stats={<Stats statsInfo={chart.release} releaseId={releaseId} />}
        />
        <ChartCard.Collectors
          summary
          collectorAmount={collectorAmount}
          avatars={
            <>
              {chart.release.topCollectors.slice(0, collectorAmount).map(node => {
                const { nftsCount } = node;
                const { id: userId, avatar, username, publicAddress, webappUri } = node.user;

                return (
                  <UserHoverCard
                    key={userId}
                    trigger={
                      <ChartCard.Collectors.Wrapper>
                        <ChartCard.Collectors.AvatarContainer summary>
                          <ChartCard.Collectors.ImageWrapper summary>
                            <Avatar
                              src={avatar?.url}
                              size={
                                ChartCard.Collectors.CHART_CARD_COLLECTOR_IMAGE_SIZES.collapsed[
                                  desktop ? 'desktop' : size
                                ]
                              }
                              username={username}
                              walletAddress={publicAddress}
                              webappUri={webappUri}
                              borderRadius={0}
                              loading="lazy"
                            />
                          </ChartCard.Collectors.ImageWrapper>
                        </ChartCard.Collectors.AvatarContainer>
                        {tablet && (
                          <StyledAccessibleLink
                            onClick={e => e.stopPropagation()}
                            pathnameUri={webappUriPath(webappUri || '')}
                          >
                            <Name>{username}</Name>
                            <Mints>
                              {nftsCount} edition{nftsCount > 1 && 's'}
                            </Mints>
                          </StyledAccessibleLink>
                        )}
                      </ChartCard.Collectors.Wrapper>
                    }
                    userHoverCardContent={<UserHoverCardContent userId={userId} />}
                  />
                );
              })}
            </>
          }
        />
      </ChartCard.InfoAndCollectors>
      {tablet && (
        <ChartCard.Button
          label="Collect Now"
          href={webappUriPath(webappUri || '')}
          onClick={() => {
            trackClickEvent({
              event: 'Collect Now Clicked',
              properties: {
                associationId: releaseId,
                associationType: 'Release',
                sourceComponent: 'Charts',
              },
            });
          }}
        />
      )}
      <ChartCard.Actions
        staticActions={<ChartCardThreeDotMenu release={chart.release} />}
        hoverActions={
          <span onClick={e => e.stopPropagation()}>
            <LikeReleaseButton releaseId={releaseId} disabled={false} source="Chart" />
          </span>
        }
      />
    </ChartCard>
  );
};

const StyledAccessibleLink = styled(AccessibleLink, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  maxWidth: '100%',
});

const Name = styled('div', {
  title: 'XXS',
  marginTop: 4,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Mints = styled('div', {
  base: 'XS',
  color: '$base500',
});
